import { createRoot } from "react-dom/client";
import App from "./App";
// Eugene 2026-05-29: self-host шрифтов (вместо Google Fonts CDN) — быстрее первый
// рендер, работает за VPN/в РФ без риска блокировки CDN. woff2 бандлятся Vite.
import "@fontsource-variable/inter";
import "@fontsource/space-grotesk/400.css";
import "@fontsource/space-grotesk/500.css";
import "@fontsource/space-grotesk/600.css";
import "@fontsource/space-grotesk/700.css";
import "./index.css";
import { initPixels, trackPageView } from "./lib/pixels";
import { captureLeadTouch } from "./lib/tracking";
import { installAudioBus } from "./lib/audio-bus";
import { registerServiceWorker } from "./lib/registerSW";
import { installUserJourney } from "./lib/user-journey";
import { ErrorBoundary } from "./components/error-boundary";
import { isChunkLoadError, reloadOnceForChunk } from "./lib/chunkReload";
import { installAutoRecenter } from "./lib/autoRecenter";
import { debugLog } from "./lib/debugLog";

if (!window.location.hash) {
  window.location.hash = "#/";
}

// Capture PWA install prompt for later use
window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  (window as any).__pwaInstallPrompt = e;
});

// Chunk-reload guards — регистрируем СРАЗУ (до render): render тянет lazy-чанки,
// а вкладка, открытая ДО деплоя, может получить 404 на старый hash-чанк →
// один guarded reload берёт свежий index.html. См. lib/chunkReload.ts.
try {
  window.addEventListener("vite:preloadError", () => { reloadOnceForChunk(); });
  window.addEventListener("error", (e) => {
    if (isChunkLoadError((e as any)?.error ?? (e as any)?.message)) reloadOnceForChunk();
  });
  window.addEventListener("unhandledrejection", (e) => {
    if (isChunkLoadError((e as PromiseRejectionEvent)?.reason)) reloadOnceForChunk();
  });
} catch (e) { console.warn("[startup] chunk-reload guard failed", e); }

// Eugene 2026-06-04 (Босс — белый экран на LTE без VPN): монтируем UI ПЕРВЫМ
// (createRoot ниже), а второстепенные side-effects — аналитику/lead/audio-bus/
// SW/journey/recenter/debug — откладываем на ПОСЛЕ first paint через idle.
// Раньше всё это выполнялось ДО createRoot и задерживало монтаж React. Каждый
// вызов изолирован try/catch (side-effect не валит рендер; см. iOS-чёрный-экран).
function whenIdle(fn: () => void): void {
  const ric = (window as unknown as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => void }).requestIdleCallback;
  if (ric) ric(fn, { timeout: 3000 }); else setTimeout(fn, 1);
}
whenIdle(() => {
  try { initPixels(); } catch (e) { console.warn("[startup] initPixels failed", e); }
  try { trackPageView(); } catch (e) { console.warn("[startup] trackPageView failed", e); }
  try { captureLeadTouch().catch(() => undefined); } catch (e) { console.warn("[startup] captureLeadTouch failed", e); }
  try { installAudioBus(); } catch (e) { console.warn("[startup] installAudioBus failed", e); }
  try { registerServiceWorker(); } catch (e) { console.warn("[startup] registerServiceWorker failed", e); }
  try { installUserJourney(); } catch (e) { console.warn("[startup] installUserJourney failed", e); }
  try { installAutoRecenter(); } catch (e) { console.warn("[startup] installAutoRecenter failed", e); }
  try {
    debugLog(`[Nav] первичная загрузка: ${window.location.hash || "/"}`);
    window.addEventListener("hashchange", () => { debugLog(`[Nav] hash → ${window.location.hash}`); });
  } catch (e) { /* no-op */ }
});

// Eugene 2026-05-25 (Босс) — ТОТАЛЬНАЯ защита от чёрного экрана: оборачиваем
// всё дерево App в top-level ErrorBoundary. Раньше boundary стоял только на
// части роутов (withBoundary), а «/» (LandingPage) и App-провайдеры были БЕЗ
// него → один throw при module-load/first-render любого компонента = пустая
// тёмная страница (React не монтировался, ничего не рендерилось). Теперь
// любой single-throw показывает красный фолбэк + шлёт стек на /api/_client-error
// (componentDidCatch) → мы видим реальную причину в логах вместо «чёрного экрана».
createRoot(document.getElementById("root")!).render(
  <ErrorBoundary pageName="root">
    <App />
  </ErrorBoundary>
);
